import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, Col, Radio, Checkbox, Tooltip } from 'antd'
import { RadioChangeEvent } from 'antd/lib/radio'
import { Country, EmployeeNameSearchInfo, EmployeeWithRole } from '../../types'
import { PerModal } from '../../components/custom_components/Modal'
import { EmployeeSearchSelect } from '../../components/custom_components/EmployeeSearchSelect'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerText from '../../components/custom_components/Text'
import RadioGroup from 'antd/lib/radio/group'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
const { Option } = Select

interface GlobalAdminAddRoleModalProps {
  open: boolean
  countryOptions: Country[]
  roleType?: 'admin' | 'readonly' | 'downloader'
  selectedEmployee?: EmployeeNameSearchInfo
  selectedCountry?: Country
  onCancel: () => void
  saveChanges: () => Promise<void>
  onSelectEmployee: (employee: EmployeeNameSearchInfo) => void
  setSelectedCountry: (country: Country) => void
  setRoleType: (roleType: 'admin' | 'readonly') => void
  hrVisibilityExcluded: boolean
  setHrVisibilityExcluded: (value: boolean) => void
  infoboxData: EmployeeWithRole[]
  error?: string
}

export const GlobalAdminAddRoleModal: FC<GlobalAdminAddRoleModalProps> = ({
  open,
  onCancel,
  countryOptions,
  roleType,
  saveChanges,
  onSelectEmployee,
  setSelectedCountry,
  selectedEmployee,
  selectedCountry,
  setRoleType,
  hrVisibilityExcluded,
  setHrVisibilityExcluded,
  infoboxData,
  error,
}) => {
  const { t } = useTranslation()
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)

  const selectedEmployeeAlreadyHasDowloaderRole =
    !!selectedEmployee && !!infoboxData.find((e) => e.id === selectedEmployee.id && e.hasDownloaderRole)

  const allRadioDisabled = selectedEmployeeAlreadyHasDowloaderRole && !countryOptions.length

  const toolTipText = () => {
    if (allRadioDisabled) {
      return ''
    } else if (!selectedEmployee || !roleType) {
      return t('globalAdminView.modal.employeeAndRoleValuesMissing')
    } else if (['admin', 'readonly'].includes(roleType) && !selectedCountry) {
      return t('globalAdminView.modal.countryValueMissing')
    }
    return t('global.button.save')
  }

  const onSave = async () => {
    toggleSubmitting(true)
    await saveChanges()
    toggleSubmitting(false)
  }

  return (
    <PerModal
      title={{ heading: t('globalAdminView.modal.addRoleTitle') }}
      size="large"
      visible={open}
      onCancel={onCancel}
      footer={{
        cancelBtn: {
          onClick: onCancel,
        },
        confirmBtn: {
          onClick: async () => {
            await onSave()
          },
          disabled: !selectedEmployee || !roleType || (['admin', 'readonly'].includes(roleType) && !selectedCountry),
          isProcessing: { active: isSubmitting },
          tooltip: toolTipText() || undefined,
        },
      }}
    >
      <>
        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('globalAdminView.modal.employee.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <EmployeeSearchSelect
              selectedValue={selectedEmployee}
              onSelectValue={onSelectEmployee}
              notFoundContentText={t('globalAdminView.modal.employee.notFoundContent')}
              placeholderText={t('globalAdminView.modal.employee.placeholder')}
            />
          </Col>
        </PerFlexRow>

        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('globalAdminView.modal.type.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <RadioGroup onChange={(evt: RadioChangeEvent) => setRoleType(evt.target.value)} value={roleType} disabled={!selectedEmployee}>
              <Tooltip title={!countryOptions.length ? t('globalAdminView.modal.country.noOptionsAvailable') : ''}>
                <Radio value={'admin'} disabled={!countryOptions.length}>
                  {t('globalAdminView.modal.type.radioAdmin')}
                </Radio>
              </Tooltip>
              <Tooltip title={!countryOptions.length ? t('globalAdminView.modal.country.noOptionsAvailable') : ''}>
                <Radio value={'readonly'} disabled={!countryOptions.length}>
                  {t('globalAdminView.modal.type.radioReadonly')}
                </Radio>
              </Tooltip>
              <Tooltip
                title={selectedEmployeeAlreadyHasDowloaderRole ? t('globalAdminView.modal.selectedEmployeeAlreadyHasDowloaderRole') : ''}
              >
                <Radio value={'downloader'} disabled={selectedEmployeeAlreadyHasDowloaderRole}>
                  {t('globalAdminView.modal.type.radioDownloader')}
                </Radio>{' '}
              </Tooltip>
            </RadioGroup>
          </Col>
        </PerFlexRow>

        {!!roleType && ['admin', 'readonly'].includes(roleType) && (
          <>
            <PerFlexRow direction="row" mb="s">
              <Col span={4}>
                <PerFlexRow align="middle" className="fullHeight">
                  <PerText color="secondary">{t('globalAdminView.modal.country.label')}</PerText>
                </PerFlexRow>
              </Col>
              <Col span={20}>
                <Select
                  placeholder={t('globalAdminView.modal.country.placeholder')}
                  value={selectedCountry}
                  onChange={(value: Country) => {
                    setSelectedCountry(value)
                  }}
                  className="fullWidth"
                  disabled={!selectedEmployee}
                >
                  {[...countryOptions]
                    .sort((a: Country, b: Country) => t(`global.country.${a}`).localeCompare(t(`global.country.${b}`)))
                    .map((country: Country, index: number) => (
                      <Option key={`country-${index}`} value={country}>
                        <PerText display="inline">{t(`global.country.${country}`)}</PerText>
                      </Option>
                    ))}
                </Select>
              </Col>
            </PerFlexRow>

            <PerFlexRow direction="row" mb="xxxs">
              <Col span={4}>
                <PerFlexRow align="middle" className="fullHeight">
                  <PerText color="secondary">{t('globalAdminView.modal.hrVisibility.label')}</PerText>
                </PerFlexRow>
              </Col>
              <Col span={20}>
                <Checkbox
                  name="checkHRVisibility"
                  checked={hrVisibilityExcluded}
                  onChange={(e: CheckboxChangeEvent) => setHrVisibilityExcluded(e.target.checked)}
                  disabled={!selectedEmployee}
                >
                  <PerText color={selectedEmployee ? 'default' : undefined}>{t('globalAdminView.modal.hrVisibility.checkbox')}</PerText>
                </Checkbox>
              </Col>
            </PerFlexRow>
          </>
        )}
        {error && (
          <PerText color="error" fontSize="m" mt="m">
            {error}
          </PerText>
        )}
      </>
    </PerModal>
  )
}
